import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, afterNextRender, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

export enum CONSENT_SERVICES {
  ADOBE = 'Adobe Analytics',
  AKAMAI = 'Akamai',
  HERE = 'Here',
  GUEST_MANAGEMENT = 'Guest Management of Press Events',
  LOGIN = 'Mercedes me Login',
  AZURE = 'Microsoft Azure',
  WEBSITE = 'The Me Media Website',
  USERCENTRICS = 'Usercentrics Consent Management Platforms',
  YOUTUBE = 'YouTube Video',
  GTM = 'Google Tag Manager',
  GA4 = 'Google Analytics 4',
}

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  /**
   * Observable subject for changed consents
   */
  public changedConsent = new Subject<Record<string, boolean>>();

  private _consents = {
    [CONSENT_SERVICES.ADOBE]: false,
    [CONSENT_SERVICES.AKAMAI]: false,
    [CONSENT_SERVICES.AZURE]: false,
    [CONSENT_SERVICES.HERE]: false,
    [CONSENT_SERVICES.GUEST_MANAGEMENT]: false,
    [CONSENT_SERVICES.LOGIN]: false,
    [CONSENT_SERVICES.USERCENTRICS]: false,
    [CONSENT_SERVICES.WEBSITE]: false,
    [CONSENT_SERVICES.YOUTUBE]: false,
    [CONSENT_SERVICES.GTM]: false,
    [CONSENT_SERVICES.GA4]: false,
  };

  private _isInitialized = false;

  constructor() {
    afterNextRender(() => {
      this.loadUserCentricsScript();
    });
  }

  public listenToConsentEvents() {
    if (!isPlatformBrowser(this.platformId)) return;

    window.addEventListener('onConsentStatusChange', (event: any) => {
      if (event.detail?.event === 'consent_status') {
        this.updateConsents(event.detail);
        this.changedConsent.next({ ...this._consents });
      }
    });

    window.addEventListener('usercentrics-initialized', () => {
      // Update UserCentrics language based on the current app language
      this._isInitialized = true;
      this._setUCLang();
    });

    this.translateService.onLangChange.subscribe(() => {
      this._setUCLang();
    });
  }

  private loadUserCentricsScript() {
    if (!isPlatformBrowser(this.platformId)) return;

    if (typeof window.UC_UI === 'undefined') {
      // Wait for the script to be loaded from index.html
      window.addEventListener('usercentrics-ready', () => {
        this.initializeUserCentrics();
      });
    } else {
      this.initializeUserCentrics();
    }
  }

  private initializeUserCentrics() {
    if (!isPlatformBrowser(this.platformId)) return;

    if (!window.UC_UI) {
      console.warn('UserCentrics script is not loaded yet. Waiting for initialization...');
      return;
    }

    this._isInitialized = true;
    this._setUCLang();

    window.addEventListener('onConsentStatusChange', (event: any) => {
      if (event.detail?.event === 'consent_status') {
        this.updateConsents(event.detail);
        this.changedConsent.next({ ...this._consents });
      }
    });

    this.translateService.onLangChange.subscribe(() => {
      this._setUCLang();
    });
  }

  private _setUCLang() {
    if (!isPlatformBrowser(this.platformId)) return;

    if (window.UC_UI && this._isInitialized) {
      if (window.UC_UI.changeLanguage && this.translateService.currentLang()) {
        const langShort = this.translateService.currentLang()?.split('-')[0];
        window.UC_UI.changeLanguage(langShort);
      }
    }
  }

  public getSettings() {
    if (!isPlatformBrowser(this.platformId)) return null;

    if (window.UC_UI) {
      return window.UC_UI.getSettings();
    } else {
      return null;
    }
  }

  public consentedTo(serviceKey: CONSENT_SERVICES) {
    return this._consents[serviceKey];
  }

  public async consentTo(consentService: CONSENT_SERVICES) {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!window.UC_UI) return;

    const id: string = window.UC_UI.getServicesBaseInfo().find((service: any) => service.name === consentService)?.id;
    id && (await window.UC_UI.acceptService(id));
  }

  public showInfoModal() {
    this.router.navigate(['legal/settings']);
  }

  public getGoogleMapsBackgroundImageUrl(latitude: number, longitude: number, zoom: number): string {
    return `https://privacy-proxy-server.usercentrics.eu/googleMaps?center=${latitude},${longitude}&size=1120x560&zoom=${zoom}`;
  }

  public getYoutubeVideoBackgroundImageUrl(videoId: string): string {
    return `https://privacy-proxy-server.usercentrics.eu/video/youtube/${videoId}-poster-image`;
  }

  updateConsents(consentData: any) {
    const { event: _event, type: _type, ...newConsents } = consentData;
    this._consents = { ...this._consents, ...newConsents };
  }
}
